// src/components/carpal-bones/SubImageAnnotator.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash'; // import debounce from lodash
import { saveSession, getSessionByImageNameAndEmail } from '../../indexedDB';
import { flushSync } from 'react-dom';

import {
  FaDownload,
  FaUndo,
  FaSave,
  FaMinus,
  FaEraser,
  FaPaintBrush,
  FaCircle,
  FaDrawPolygon,
  FaPencilAlt,
  FaSearchMinus,
  FaCog,
  FaAdjust,
  FaSun,
  //FaCheck, // Importa FaCheck per il pulsante "Close Polygon"
} from 'react-icons/fa';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import '../tools.css';

function SubImageAnnotator({
  subImage,
  collectAnnotations,
  email,
  //tipo,
  posizione,
  //regione,
  sesso,
  realAge = '',
  defaultMode = 'polygon',
  allowModeChange = false,
  selectableColors = ['blue', 'red'],
  initialAnnotations = null,
  updateSessionsCount,
}) {
  const tool = 'carpal-bones';
  //console.log('Received sex in subimageannotator:', sesso); // Debug
  // Reference to track if panning has occurred
  const hasPanned = useRef(false);

  // Function to compute centroid of points
  function computeCentroid(points) {
    const sum = points.reduce(
      (acc, point) => {
        acc.x += point.x;
        acc.y += point.y;
        return acc;
      },
      { x: 0, y: 0 }
    );
    return { x: sum.x / points.length, y: sum.y / points.length };
  }

  // Debounced saveCurrentSession function
  // eslint-disable-next-line
  const saveCurrentSessionDebounced = useCallback(
    debounce(async (sessionData) => {
      //setIsSaving(true);
      await saveSession(sessionData);
      if (updateSessionsCount) {
        updateSessionsCount();
      }
      //setIsSaving(false);
  }, 1000), // Debounce time of 1000ms (1 second)
  [] // Empty dependency array to create the function only once
  );

  // eslint-disable-next-line
  //const [isSaving, setIsSaving] = useState(false);

  // Initialize mouseDownPosition with null values
  const [mouseDownPosition, setMouseDownPosition] = useState({ x: null, y: null });
  const panningThreshold = 5; // pixels

  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Memoize selectableColors if passed inline as a prop
  const memoizedSelectableColors = useMemo(() => selectableColors, [selectableColors]);

  const lineKeys = useMemo(
    () => memoizedSelectableColors.map((color, index) => `line${index + 1}`),
    [memoizedSelectableColors]
  );

  const polygonKeysInitial = useMemo(() => ['polygon1'], []);
  const [coordinates, setCoordinates] = useState(() =>
    lineKeys.concat(polygonKeysInitial).reduce((acc, key) => ({ ...acc, [key]: [] }), {})
  );

  const [closedPolygons, setClosedPolygons] = useState(() =>
    polygonKeysInitial.reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );

  const [ratios, setRatios] = useState({});
  const [areas, setAreas] = useState({});
  const [estimatedAge, setEstimatedAge] = useState(null);
  const [currentLine, setCurrentLine] = useState(lineKeys[0]);
  const [currentMode, setCurrentMode] = useState(defaultMode);
  const [lineThickness, setLineThickness] = useState(1);
  const [pointRadius, setPointRadius] = useState(2);
  const clickDisabled = useRef(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const minZoom = 0.5;
  const maxZoom = 5;

  // States for settings and save menu
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [activeSetting, setActiveSetting] = useState('');
  const [contrast, setContrast] = useState(100);
  const [brightness, setBrightness] = useState(100);

  // State for panning and dragging
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggingPoint, setDraggingPoint] = useState(null);

  // Memoize subImage if needed
  // eslint-disable-next-line
  const memoizedSubImage = useMemo(() => subImage, [subImage.name, subImage.dataURL]);

  // Definition of currentPolygonKey state
  const [currentPolygonKey, setCurrentPolygonKey] = useState(null);

  // Handler for settings
  const handleSettingClick = (setting) => {
    setActiveSetting(setting === activeSetting ? '' : setting);
  };

  const handleContrastChange = (e) => setContrast(Number(e.target.value));
  const handleBrightnessChange = (e) => setBrightness(Number(e.target.value));
  const handleResetContrast = () => setContrast(100);
  const handleResetBrightness = () => setBrightness(100);
  const handleResetLineThickness = () => setLineThickness(1);
  const handleResetPointRadius = () => setPointRadius(2);

  // Helper to check if the mouse is near a point
  const isMouseOverAnyPoint = (x, y) => {
    const threshold = pointRadius / zoomLevel + 2; // tolerance for proximity
    return Object.values(coordinates).some(points =>
      points.some(point => {
        const dx = x - point.x;
        const dy = y - point.y;
        return Math.sqrt(dx * dx + dy * dy) < threshold;
      })
    );
  };

  // Color map for lines and polygons
  const colorMap = useMemo(() => {
    const map = {};
    Object.keys(coordinates).forEach(key => {
      if (key.startsWith('polygon')) {
        if (key === 'polygon2') map[key] = 'red'; // Solo 'polygon2' è rosso
        else map[key] = 'blue'; // Altri poligoni sono blu
      } else {
        const colorIndex = parseInt(key.replace('line', ''), 10);
        map[key] = memoizedSelectableColors[colorIndex - 1] || '#f0f0f0';
      }
    });
    return map;
  }, [coordinates, memoizedSelectableColors]);

  const getLineColor = useCallback((lineKey) => {
    return colorMap[lineKey] || '#f0f0f0';
  }, [colorMap]);

  const handleModeToggle = useCallback(() => {
    if (allowModeChange) {
      setCurrentMode((prevMode) => (prevMode === 'line' ? 'polygon' : 'line'));
    }
  }, [allowModeChange]);

  const handleLineChange = useCallback((newLine) => {
    setCurrentLine(newLine);
    if (currentMode === 'polygon') {
      if (newLine === 'line1') { // Blue
        // Find next available index for blue polygons, skipping 'polygon2'
        let nextIndex = 1;
        while (`polygon${nextIndex}` in coordinates) {
          nextIndex++;
          if (nextIndex === 2) nextIndex++; // Skip 'polygon2' which is red
        }
        const newPolygonKey = `polygon${nextIndex}`;
        setCoordinates(prev => ({ ...prev, [newPolygonKey]: [] }));
        setClosedPolygons(prev => ({ ...prev, [newPolygonKey]: false }));
        setCurrentPolygonKey(newPolygonKey);
      } else if (newLine === 'line2') { // Red
  // Use 'polygon2' for red
        if (!coordinates['polygon2']) {
          setCoordinates(prev => ({ ...prev, 'polygon2': [] }));
          setClosedPolygons(prev => ({ ...prev, 'polygon2': false }));
        }
        setCurrentPolygonKey('polygon2');
      }
    }
  }, [currentMode, coordinates]);

  // Convert sex from "M"/"F" to 1/0
  const convertSex = useCallback((sesso) => {
    if (sesso === 'M') return 1;
    if (sesso === 'F') return 0;
  return null; // Return null if sex is not valid
  }, []);

  // Store numeric sex value
  const sexValue = useMemo(() => convertSex(sesso), [sesso, convertSex]);


  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && draggingPoint) {
        // Handle dragging of points
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        // Update the position of the dragged point
        setCoordinates((prevCoordinates) => {
          const updatedCoordinates = { ...prevCoordinates };
          updatedCoordinates[draggingPoint.key][draggingPoint.pointIndex] = {
            x: imageX,
            y: imageY,
          };
          return updatedCoordinates;
        });
        clickDisabled.current = true; // Disable click while dragging

        // Set cursor to 'move' during dragging
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'move';
        }
      } else if (isPanning) {
        e.preventDefault(); // Prevent default behavior during panning

        const newPanOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        };
        setPanOffset(newPanOffset);
        hasPanned.current = true; // Indicate that panning has occurred

        // Set cursor to 'grabbing' during panning
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'grabbing';
        }
      } else if (mouseDownPosition.x !== null && mouseDownPosition.y !== null) {
        const deltaX = e.clientX - mouseDownPosition.x;
        const deltaY = e.clientY - mouseDownPosition.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > panningThreshold && zoomLevel > 1) {
          setIsPanning(true);
          setPanStart({
            x: mouseDownPosition.x - panOffset.x,
            y: mouseDownPosition.y - panOffset.y,
          });
          clickDisabled.current = true; // Prevent adding a point during panning
        } else {
          // Set cursor to 'crosshair' when not panning or dragging
          if (canvasRef.current) {
            canvasRef.current.style.cursor = 'crosshair';
          }
        }
      } else {
  // Check if the mouse is over a point
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        if (isMouseOverAnyPoint(imageX, imageY)) {
          canvasRef.current.style.cursor = 'move'; // Change cursor to 'move' when over a point
        } else {
          canvasRef.current.style.cursor = 'crosshair'; // Otherwise, 'crosshair'
        }
      }
    },
    // eslint-disable-next-line
    [
      isDragging,
      draggingPoint,
      isPanning,
      panStart.x,
      panStart.y,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      mouseDownPosition,
      panningThreshold,
      coordinates,
      isMouseOverAnyPoint,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setIsDragging(false);
    setDraggingPoint(null);
  setMouseDownPosition({ x: null, y: null }); // Reset mouse position
  // Restore cursor to 'crosshair'
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'crosshair';
    }
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      if (isDragging || isPanning || hasPanned.current) {
        hasPanned.current = false; // Reset after ignoring the click
        return; // Ignore click
      }
      if (clickDisabled.current) {
        clickDisabled.current = false; // Reset flag
        return; // Do not add a new point
      }

      if (!currentPolygonKey) {
        alert('Select a color to start annotation.');
        return;
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = (x - panOffset.x) * scaleX / zoomLevel;
      const imageY = (y - panOffset.y) * scaleY / zoomLevel;

      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        const key = currentPolygonKey;

        if (currentMode === 'line') {
          if (updatedCoordinates[key].length < 2) {
            updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
          }
        } else if (currentMode === 'polygon') {
          updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
        }

        return updatedCoordinates;
      });
  // Reset hasPanned.current after adding a point
  hasPanned.current = false;
    },
    [
      isDragging,
      isPanning,
      clickDisabled,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      currentMode,
      currentPolygonKey,
    ]
  );

  const handleMouseDown = useCallback(
    (e) => {
  hasPanned.current = false; // Reset the flag
      setMouseDownPosition({ x: e.clientX, y: e.clientY });

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = (x - panOffset.x) * scaleX / zoomLevel;
      const imageY = (y - panOffset.y) * scaleY / zoomLevel;

      let foundPoint = null;
      let foundKey = null;

      Object.entries(coordinates).forEach(([key, points]) => {
        points.forEach((point, idx) => {
          const dx = imageX - point.x;
          const dy = imageY - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < pointRadius / zoomLevel + 2) {
            foundPoint = idx;
            foundKey = key;
          }
        });
      });

      if (foundPoint !== null && foundKey !== null) {
        // Start dragging the point
        setIsDragging(true);
        setDraggingPoint({ key: foundKey, pointIndex: foundPoint });
        clickDisabled.current = false; // Reset del flag
      } else {
        clickDisabled.current = false; // Allow adding a new point
      }
    },
    [panOffset.x, panOffset.y, zoomLevel, pointRadius, coordinates]
  );

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const zoomAmount = e.deltaY * -0.001;
      const newZoomLevel = Math.min(Math.max(zoomLevel + zoomAmount, minZoom), maxZoom);
      const zoomRatio = newZoomLevel / zoomLevel;

      const newPanOffset = {
        x: x - (x - panOffset.x) * zoomRatio,
        y: y - (y - panOffset.y) * zoomRatio,
      };

      setZoomLevel(newZoomLevel);
      setPanOffset(newPanOffset);
    },
    [zoomLevel, panOffset.x, panOffset.y, minZoom, maxZoom]
  );

  const handleReset = useCallback(() => {
  // Reset all annotations
    const resetCoordinates = lineKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {});
    resetCoordinates['polygon1'] = [];

    setCoordinates(resetCoordinates);
    setClosedPolygons({
      polygon1: false,
    });
    setAreas({});
    setRatios({});
    setEstimatedAge(null);
    setCurrentPolygonKey(null);
  }, [lineKeys]);

  const handleUndoLastPoint = useCallback(() => {
    if (currentMode === 'line') {
      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        if (updatedCoordinates[currentLine].length > 0) {
          updatedCoordinates[currentLine] = updatedCoordinates[currentLine].slice(0, -1);
        }
        return updatedCoordinates;
      });
    } else if (currentMode === 'polygon') {
      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        if (updatedCoordinates[currentPolygonKey].length > 0) {
          updatedCoordinates[currentPolygonKey] = updatedCoordinates[currentPolygonKey].slice(0, -1);
        }
        return updatedCoordinates;
      });
    }
  }, [currentMode, currentLine, currentPolygonKey]);

  // Function to manually close the polygon
  const handleClosePolygon = useCallback(() => {
    if (!currentPolygonKey) {
      alert('No polygon selected.');
      return;
    }

    // Ensure the current polygon has at least 3 points
    if (coordinates[currentPolygonKey].length < 3) {
      alert('A polygon must have at least 3 points to be closed.');
      return;
    }

    // Marca il poligono come chiuso
    setClosedPolygons((prev) => ({
      ...prev,
      [currentPolygonKey]: true,
    }));

    // Ottieni il colore del poligono chiuso
    const color = colorMap[currentPolygonKey];

    if (color === 'blue') {
      // Crea un nuovo poligono blu automaticamente
      let nextIndex = 1;
      while (`polygon${nextIndex}` in coordinates) {
        nextIndex++;
        if (nextIndex === 2) nextIndex++; // Salta 'polygon2' che è rosso
      }
      const newPolygonKey = `polygon${nextIndex}`;
      setCoordinates(prev => ({ ...prev, [newPolygonKey]: [] }));
      setClosedPolygons(prev => ({ ...prev, [newPolygonKey]: false }));
      setCurrentPolygonKey(newPolygonKey);
    } else {
  // If the closed polygon is red, do not automatically start a new polygon
      setCurrentPolygonKey(null);
    }
  }, [coordinates, currentPolygonKey, colorMap]);

  // Calculated/derived functions
  const calculatePolygonArea = useCallback((points) => {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area) / 2;
  }, []);

  const calculateLineLength = useCallback((points) => {
    if (points.length === 2) {
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    return 0;
  }, []);

  const saveCurrentSession = useCallback(() => {
    const sessionData = {
      subImage: {
        ...memoizedSubImage,
        name: memoizedSubImage.name,
      },
      tool,
      coordinates,
      ratios,
      areas,
      estimatedAge,
      email,
      //tipo,
      //posizione,
      //regione,
      sesso,
      realAge,
      closedPolygons, // Aggiungi lo stato dei poligoni chiusi
      currentPolygonKey, // Aggiungi la chiave del poligono corrente
      timestamp: new Date().toISOString(),
    };
    saveCurrentSessionDebounced(sessionData);
  }, [
    coordinates,
    ratios,
    areas,
    estimatedAge,
    memoizedSubImage,
    email,
    //tipo,
    //posizione,
    sesso,
    realAge,
    closedPolygons,
    currentPolygonKey,
    saveCurrentSessionDebounced, // Include in dependencies
  ]);

  // Aggiorna l'uso di useEffect per il salvataggio
  useEffect(() => {
    saveCurrentSession();
  }, [coordinates, ratios, areas, estimatedAge, saveCurrentSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      saveCurrentSessionDebounced.cancel();
    };
  }, [saveCurrentSessionDebounced]);

  // useEffect per caricare la sessione salvata
  useEffect(() => {
    const loadSavedSession = async () => {
      const savedSession = await getSessionByImageNameAndEmail(
        memoizedSubImage.name,
        email
      );
      if (savedSession) {
        setCoordinates(savedSession.coordinates || {});
        setRatios(savedSession.ratios || {});
        setAreas(savedSession.areas || {});
        setEstimatedAge(savedSession.estimatedAge || null);
        setClosedPolygons(savedSession.closedPolygons || {});

        // Imposta il poligono corrente
        if (savedSession.currentPolygonKey) {
          setCurrentPolygonKey(savedSession.currentPolygonKey);
        } else {
          // Se non esiste, imposta a null e lascia che l'utente selezioni un colore
          setCurrentPolygonKey(null);
        }
      } else if (initialAnnotations) {
        setCoordinates(initialAnnotations.coordinates || {});
        setRatios(initialAnnotations.ratios || {});
        setAreas(initialAnnotations.areas || {});
        setEstimatedAge(initialAnnotations.estimatedAge || null);
        setClosedPolygons(initialAnnotations.closedPolygons || {});

        // Imposta il poligono corrente
        if (initialAnnotations.currentPolygonKey) {
          setCurrentPolygonKey(initialAnnotations.currentPolygonKey);
        } else {
          // Se non esiste, imposta a null e lascia che l'utente selezioni un colore
          setCurrentPolygonKey(null);
        }
      }
    };
    loadSavedSession();
  }, [memoizedSubImage.name, email, initialAnnotations]);

  // Calculate area only for closed polygons
  useEffect(() => {
    const updatedAreas = {};
    const polygonKeys = Object.keys(coordinates).filter(k => k.startsWith('polygon'));
    polygonKeys.forEach((polygonKey) => {
      const points = coordinates[polygonKey];
      if (points.length >= 3 && closedPolygons[polygonKey]) {
        const centroid = computeCentroid(points);
        const pointsWithAngles = points.map((point) => {
          const angle = Math.atan2(point.y - centroid.y, point.x - centroid.x);
          return { ...point, angle };
        });
        const sortedPoints = pointsWithAngles.sort((a, b) => a.angle - b.angle);

        const area = calculatePolygonArea(sortedPoints);
        updatedAreas[polygonKey] = area;
      } else if (areas[polygonKey]) {
        updatedAreas[polygonKey] = undefined;
      }
    });
    setAreas((prevAreas) => ({ ...prevAreas, ...updatedAreas }));

    const updatedRatios = {};
    lineKeys.forEach((lineKey) => {
      const points = coordinates[lineKey];
      if (points.length === 2) {
        const length = calculateLineLength(points);
        updatedRatios[lineKey] = length;
      } else if (ratios[lineKey]) {
        updatedRatios[lineKey] = undefined;
      }
    });
    setRatios((prevRatios) => ({ ...prevRatios, ...updatedRatios }));
    // eslint-disable-next-line
  }, [coordinates, calculatePolygonArea, calculateLineLength, closedPolygons]);



  // Disegna il canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const drawCanvas = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        Object.entries(coordinates).forEach(([key, points]) => {
          const isLine = key.startsWith('line');
          const color = getLineColor(key);

          ctx.fillStyle = color;
          ctx.strokeStyle = color;

          if (isLine) {
            // Draw lines (unchanged)
            if (points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }
              ctx.lineWidth = lineThickness;
              ctx.stroke();

              // Draw points
              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          } else {
            if (points.length > 0) {
              // For polygon drawing keep the points in input order and do not auto-close after 3 points
              // This matches the canine-teeth-ai visual behaviour: only close/fill when closedPolygons[key] is true
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }

              // Only close and fill if the polygon is marked closed (logic unchanged)
              if (closedPolygons[key] && points.length > 2) {
                ctx.closePath();
                if (areas[key]) {
                  ctx.globalAlpha = 0.3;
                  ctx.fillStyle = color;
                  ctx.fill();
                  ctx.globalAlpha = 1.0;
                }
              }

              ctx.lineWidth = lineThickness;
              ctx.stroke();

              // Draw points with the same style used in canine-teeth-ai (larger visible dots)
              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, Math.max(pointRadius, 3), 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          }
        });
      };

      let animationFrameId = requestAnimationFrame(drawCanvas);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [coordinates, lineThickness, pointRadius, areas, getLineColor, closedPolygons]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Funzione per resettare la vista e salvare l'immagine
  const handleDownloadAnnotatedImage = useCallback(() => {
    // Resetta zoom e pan sincronamente per assicurarti che il DOM si aggiorni prima della cattura
    flushSync(() => {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    });

    // Effettua la cattura dopo che lo stato è stato aggiornato
    html2canvas(imageContainerRef.current, { useCORS: true }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, memoizedSubImage.name);
      }, 'image/png', 1);
    });
  }, [memoizedSubImage.name]);


  const handleSaveAnnotationsJSON = useCallback(() => {
    const data = {
      imageName: memoizedSubImage.name,
      annotations: coordinates,
      ratios: ratios,
      areas: areas,
      estimatedAge: estimatedAge,
      email: email,
      sex: sesso, // Assicurati che 'sesso' sia valorizzato
      realAge: realAge, // Aggiungi realAge
      centerPoint: memoizedSubImage.center,
      timestamp: new Date().toISOString(),
      subImageDimensions: {
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      },
      closedPolygons: closedPolygons, // Aggiungi lo stato dei poligoni chiusi
      currentPolygonKey: currentPolygonKey, // Salva la chiave del poligono corrente
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    saveAs(blob, `${memoizedSubImage.name.replace(/\.[^/.]+$/, '')}_annotations.json`);
  }, [
    memoizedSubImage,
    coordinates,
    ratios,
    areas,
    estimatedAge,
    email,
    sesso, // Assicurati che 'sesso' sia incluso nelle dipendenze
    realAge, // Aggiungi realAge alle dipendenze
    closedPolygons,
    currentPolygonKey,
  ]);


  // // Definisci il margine di errore
  // const ageErrorMargin = 2.68;

  // // Calcola l'età minima e massima
  // const lowerAge = estimatedAge !== null && !isNaN(estimatedAge)
  //   ? (estimatedAge - ageErrorMargin).toFixed(2)
  //   : null;

  // const upperAge = estimatedAge !== null && !isNaN(estimatedAge)
  //   ? (estimatedAge + ageErrorMargin).toFixed(2)
  //   : null;

  // Calculate total blue area and red area
  const { totalBlueArea, redArea } = useMemo(() => {
    let blueSum = 0;
    let redSum = 0;
    Object.keys(areas).forEach(key => {
      if (areas[key]) {
        if (colorMap[key] === 'blue') {
          blueSum += areas[key];
        } else if (colorMap[key] === 'red') {
          redSum += areas[key];
        }
      }
    });
    return { totalBlueArea: blueSum, redArea: redSum };
  }, [areas, colorMap]);

  // New useEffect to compute estimated age based on the formula
  const [ageFormula, setAgeFormula] = useState('');

  useEffect(() => {
    if (sexValue !== null && redArea > 0) {
      const ratio = totalBlueArea / redArea;
      const age = -3.253 + (0.719 * sexValue) + (20.61 * ratio);
      setEstimatedAge(age);
      setAgeFormula(`-3.253 + (0.719 * ${sexValue}) + (20.61 * (${ratio.toFixed(2)}))`);
    } else {
      setEstimatedAge(null);
      setAgeFormula('');
    }
  }, [totalBlueArea, redArea, sexValue]);

  return (
    <div className="subimage-container">
      <h3 className="subimage-title">{memoizedSubImage.name}</h3>

      <div className='tooltips'>
        <div className="tooltip">
          <p>Use 🔵 color to select edge points of the carpal bones</p>
        </div>
        <div className="tooltip">
          <p>Use 🔴 color to select edges of the carpal surface</p>
        </div>
      </div>

      <div className="subimage-topbar">
        {allowModeChange && (
          <button
            onClick={handleModeToggle}
            className="topbar-button"
            title={
              currentMode === 'line' ? 'Switch to Polygon Mode' : 'Switch to Line Mode'
            }
          >
            {currentMode === 'line' ? (
              <FaDrawPolygon className="topbar-icon" />
            ) : (
              <FaPencilAlt className="topbar-icon" />
            )}
          </button>
        )}

        <div className="icon-group">
          {lineKeys.map((line) => (
            <button
              key={line}
              onClick={() => handleLineChange(line)}
              className={`topbar-button ${currentLine === line ? 'active-line' : ''}`}
              title={`Color ${colorMap[line]}`}
              style={{ backgroundColor: getLineColor(line) }}
            >
              <FaMinus className="topbar-icon" />
            </button>
          ))}
          <button onClick={handleClosePolygon} className="topbar-button" title="Close Polygon">
            <FaDrawPolygon className="topbar-icon" />
          </button>
        </div>

        <div className="icon-group">
          <button onClick={handleUndoLastPoint} className="topbar-button" title="Undo Last Point">
            <FaUndo className="topbar-icon" />
          </button>
          <button onClick={handleReset} className="topbar-button" title="Reset">
            <FaEraser className="topbar-icon" />
          </button>
        </div>

        <div className="icon-group">
          <button
            onClick={handleResetZoom}
            className="topbar-button"
            title="Reset Zoom"
          >
            <FaSearchMinus className="topbar-icon" />
          </button>
        </div>

        <div className="icon-group">
          <button
            onClick={() => setShowSaveMenu(!showSaveMenu)}
            className="topbar-button"
            title="Save"
          >
            <FaSave className="topbar-icon" />
          </button>
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className={`topbar-button${showSettingsMenu ? ' active-settings' : ''}`}
            title="Settings"
          >
            <FaCog className="topbar-icon" />
          </button>
        </div>
      </div>

      {showSettingsMenu && (
        <div className="subimage-topbar settings-menu">
          <div className="settings-icons-container">
            <button
              className={`settings-icon-button${activeSetting === 'contrast' ? ' active' : ''}`}
              onClick={() => handleSettingClick('contrast')}
              title="Contrast"
            >
              <FaAdjust className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'brightness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('brightness')}
              title="Brightness"
            >
              <FaSun className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'lineThickness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('lineThickness')}
              title="Line Thickness"
            >
              <FaPaintBrush className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'pointRadius' ? ' active' : ''}`}
              onClick={() => handleSettingClick('pointRadius')}
              title="Point Size"
            >
              <FaCircle className="topbar-icon" />
            </button>
          </div>

          <div className={`slider-container ${activeSetting === 'contrast' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={contrast}
              onChange={handleContrastChange}
            />
            <div className="slider-value">
              {contrast}%
              <span className="reset-icon" onClick={handleResetContrast}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'brightness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={brightness}
              onChange={handleBrightnessChange}
            />
            <div className="slider-value">
              {brightness}%
              <span className="reset-icon" onClick={handleResetBrightness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'lineThickness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="1"
              max="5"
              value={lineThickness}
              onChange={(e) => setLineThickness(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {lineThickness}
              <span className="reset-icon" onClick={handleResetLineThickness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'pointRadius' ? 'visible' : ''}`}>
            <input
              type="range"
              min="2"
              max="10"
              value={pointRadius}
              onChange={(e) => setPointRadius(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {pointRadius}
              <span className="reset-icon" onClick={handleResetPointRadius}>🔄</span>
            </div>
          </div>
        </div>
      )}

      {showSaveMenu && (
        <div className="subimage-topbar save-menu">
          <div className="save-buttons-container">
            <button
              onClick={handleDownloadAnnotatedImage}
              className="save-button"
              title="Save Image"
            >
              <FaDownload className="button-icon" />
              Save Image
            </button>
            <button
              onClick={handleSaveAnnotationsJSON}
              className="save-button"
              title="Save Annotations (JSON)"
            >
              <FaSave className="button-icon" />
              Save Annotations
            </button>
          </div>
        </div>
      )}
      <div className="annotator-container">
        <div
          className="image-container"
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className="image-wrapper"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              src={memoizedSubImage.dataURL}
              alt="Selected sub-image"
              ref={imageRef}
              className="annotator-image"
              crossOrigin="anonymous"
              style={{ filter: `contrast(${contrast}%) brightness(${brightness}%)` }}
              onLoad={() => {
                const img = imageRef.current;
                const canvas = canvasRef.current;
                if (img && canvas) {
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                }
              }}
            />
            <canvas
              ref={canvasRef}
              className="annotator-canvas"
              onClick={handleCanvasClick}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <div className="coordinates-container">
          {/* Mostra le aree dei poligoni chiusi separatamente per colore */}
          <div className="coordinate-box">
            <p className="point-text-small point-text-title" style={{ color: 'blue' }}>
              Carpal Bones and Ephiphyses Area (Cb): {Math.round(totalBlueArea)} px²
            </p>
          </div>
          <div className="coordinate-box">
            <p className="point-text-small point-text-title" style={{ color: 'red' }}>
              Carpal Area (Ca): {Math.round(redArea)} px²
            </p>
          </div>
        </div>

        {estimatedAge !== null && !isNaN(estimatedAge) && (
          <div className="results-footer">
            {/* Sezione Risultati */}
            <div className="results-grid single-column">
              {/* Card principale dei risultati */}
              <div className="result-card">
                <div className="result-header">
                  <h4>Carpal Bones Age Estimation</h4>
                </div>
                <div className="result-values">
                  <div className="value-item">
                    <span className="value-number">{(estimatedAge - 1.19).toFixed(2)}</span>
                    <span className="value-label">Min Age (yrs)</span>
                  </div>
                  <div className="value-divider">|</div>
                  <div className="value-item">
                    <span className="value-number">{estimatedAge.toFixed(2)}</span>
                    <span className="value-label">Estimated Age (yrs)</span>
                  </div>
                  <div className="value-divider">|</div>
                  <div className="value-item">
                    <span className="value-number">{(estimatedAge + 1.19).toFixed(2)}</span>
                    <span className="value-label">Max Age (yrs)</span>
                  </div>
                </div>
                
                <div className="meta-info">
                  <span className="region-info">Carpal Bone Analysis Method</span>
                  <span className="real-age-info">Ca/Cb Ratio: {Math.round(redArea)} / {Math.round(totalBlueArea)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {estimatedAge !== null && ageFormula && (
          <div className="bibliography-section">
            <h4>Bibliography & Formula</h4>
            <p className="bibliography-text">
              <strong>Age Formula:</strong> {ageFormula}<br/>
              <strong>Reference:</strong><br/>
              <em>Cameriere, R. & Ferrante, L. Age estimation in children by measurement of carpals and epiphyses of radius and ulna and open apices in teeth: a pilot study. Forensic Sci Int 174, 60–63 (2008).</em>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubImageAnnotator;